/**
 *
 * @module telemetryClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/telemetry.md
 *
 */

import Check from 'check-types';
import Logging from './../../../logging/entry';
import Exception from './../../exception/entry';
import HttpClient from './../../platforms/httpClient';
import Token from './../../token/entry';
import Util from './../../util/entry';
import Configuration from './../../configuration/entry';

import TelemetryClientEndpoint from './telemetryClientEndpoint';
import TelemetryClientConfiguration from './telemetryClientConfiguration';
import TelemetryPayload from './telemetryPayload';
import TelemetryResponse from './telemetryResponse';
import DustLogUtility from './../dust/dustLogUtility';
import DustUrnReference from './../dust/dustUrnReference';

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const TelemetryClientDustUrnReference = DustUrnReference.services.internal.telemetry.telemetryClient;

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const {
    like,
    instanceStrict
} = Check.assert;

/**
 *
 * @class TelemetryClient
 * @implements {ITelemetryClient}
 * @desc Provides a data client that can be used to access telemetry services.
 *
 */
export default class TelemetryClient {

    /**
     *
     * @access private
     * @param {TelemetryClientConfiguration} telemetryClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(telemetryClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'TelemetryClient(telemetryClientConfiguration, logger, httpClient)',
            get telemetryClientConfiguration() {
                return `${this.ctor} needs TelemetryClientConfiguration instance`;
            }
        };

        instanceStrict(
            telemetryClientConfiguration,
            TelemetryClientConfiguration,
            errorMessage.telemetryClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {TelemetryClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = telemetryClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc indicates the buffer should retry some action after a defined amount of time
         *
         */
        this.replyAfterHeader = 'x-mlbam-reply-after';

        /**
         *
         * @access private
         * @type {String}
         * @desc indicates a unique tracking identifier
         *
         */
        this.requestIdHeader = 'x-request-id';

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(TelemetryClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @param {Array<TelemetryPayload>} telemetryPayloads The collection of TelemetryPayload(s) to send.
     * @desc Posts an event collection to the telemetry service.
     * @returns {Promise<TelemetryResponse>} Optional information and/or instructions about the telemetry service.
     *
     */
    postEvents(accessToken, telemetryPayloads) {

        const errorMessage = {
            method: 'TelemetryClient.postEvents(accessToken, telemetryPayloads)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            },
            get telemetryPayloads() {
                return `${this.method} needs Array of TelemetryPayload instances`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        Check.assert(
            (() => {
                return Check.all(telemetryPayloads.map((payload) => {
                    return like(payload, TelemetryPayload);
                }));
            })(),
            errorMessage.telemetryPayloads,
            InvalidArgumentException
        );

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            TelemetryClientEndpoint.postEvent,
            telemetryPayloads
        );

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: TelemetryClient.name,
            urn: TelemetryClientDustUrnReference.postEvents,
            payload,
            method: HttpMethod.GET
        });

        logger.info(TelemetryClient.name, `Posting telemetry events: Count: ${telemetryPayloads.length}`);

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { headers } = response;
            const { replyAfterHeader, requestIdHeader } = this;

            const replyAfter = headers.get(replyAfterHeader) || null;
            const requestId = headers.get(requestIdHeader) || null;

            return Promise.resolve(
                new TelemetryResponse(replyAfter, requestId)
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @param {Array<TelemetryPayload>} telemetryPayloads The collection of TelemetryPayload(s) to send.
     * @desc Posts a dust event collection to the telemetry service.
     * @note do NOT post dustEvents to the dustSink
     * @returns {Promise<TelemetryResponse>} Optional information and/or instructions about the telemetry service.
     *
     */
    postDustEvents(accessToken, telemetryPayloads) {

        const errorMessage = {
            method: 'TelemetryClient.postDustEvents(accessToken, telemetryPayloads)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            },
            get telemetryPayloads() {
                return `${this.method} needs Array of TelemetryPayload instances`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        Check.assert(
            (() => {
                return Check.all(telemetryPayloads.map((payload) => {
                    return like(payload, TelemetryPayload);
                }));
            })(),
            errorMessage.telemetryPayloads,
            InvalidArgumentException
        );

        const { logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            TelemetryClientEndpoint.dustEvent,
            telemetryPayloads
        );

        logger.info(TelemetryClient.name, `Posting dust events: Count: ${telemetryPayloads.length}`);

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response);

        }).then((response) => {

            const { headers } = response;
            const { replyAfterHeader, requestIdHeader } = this;

            const replyAfter = headers.get(replyAfterHeader) || null;
            const requestId = headers.get(requestIdHeader) || null;

            return Promise.resolve(new TelemetryResponse(replyAfter, requestId));

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {TelemetryClientEndpoint} rel
     * @param {Array<TelemetryPayload>} body
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestHeaders = Object.assign({}, headers);
        const requestBody = JSON.stringify(body);

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi,
            accessToken.token);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/internal/telemetry/telemetryClient.js